import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with auth header
export const createApiClient = (token) => {
  const client = axios.create({
    baseURL: API_BASE,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return client;
};

// API functions
export const api = {
  // Generic methods
  get: (token, path) => 
    axios.get(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  post: (token, path, data) => 
    axios.post(`${API_BASE}${path}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  put: (token, path, data) => 
    axios.put(`${API_BASE}${path}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  delete: (token, path) => 
    axios.delete(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Auth
  login: (email, password) => 
    axios.post(`${API_BASE}/auth/login`, { email, password }),
  
  getMe: (token) => 
    axios.get(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),

  // Clients
  getClients: (token) => 
    axios.get(`${API_BASE}/clients`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getClient: (token, clientId) => 
    axios.get(`${API_BASE}/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createClient: (token, clientData) =>
    axios.post(`${API_BASE}/clients`, clientData, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateClient: (token, clientId, clientData) =>
    axios.put(`${API_BASE}/clients/${clientId}`, clientData, { headers: { Authorization: `Bearer ${token}` } }),
  
  deleteClient: (token, clientId) =>
    axios.delete(`${API_BASE}/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateNetWorth: (token, clientId, netWorth) => 
    axios.put(`${API_BASE}/clients/${clientId}/net-worth`, { netWorth }, { headers: { Authorization: `Bearer ${token}` } }),

  // Individuals
  getIndividuals: (token, clientId) => 
    axios.get(`${API_BASE}/clients/${clientId}/individuals`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getIndividual: (token, clientId, individualId) => 
    axios.get(`${API_BASE}/clients/${clientId}/individuals/${individualId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createIndividual: (token, clientId, data) =>
    axios.post(`${API_BASE}/clients/${clientId}/individuals`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateIndividual: (token, clientId, individualId, data) =>
    axios.put(`${API_BASE}/clients/${clientId}/individuals/${individualId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  deleteIndividual: (token, clientId, individualId) =>
    axios.delete(`${API_BASE}/clients/${clientId}/individuals/${individualId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Entities
  getEntities: (token, clientId) => 
    axios.get(`${API_BASE}/clients/${clientId}/entities`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getEntity: (token, clientId, entityId) => 
    axios.get(`${API_BASE}/clients/${clientId}/entities/${entityId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createEntity: (token, clientId, data) =>
    axios.post(`${API_BASE}/clients/${clientId}/entities`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateEntity: (token, clientId, entityId, data) =>
    axios.put(`${API_BASE}/clients/${clientId}/entities/${entityId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  deleteEntity: (token, clientId, entityId) =>
    axios.delete(`${API_BASE}/clients/${clientId}/entities/${entityId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Advisers
  getAdvisers: (token, clientId) => 
    axios.get(`${API_BASE}/clients/${clientId}/advisers`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getAdviser: (token, clientId, adviserId) => 
    axios.get(`${API_BASE}/clients/${clientId}/advisers/${adviserId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createAdviser: (token, clientId, data) =>
    axios.post(`${API_BASE}/clients/${clientId}/advisers`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateAdviser: (token, clientId, adviserId, data) =>
    axios.put(`${API_BASE}/clients/${clientId}/advisers/${adviserId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  deleteAdviser: (token, clientId, adviserId) =>
    axios.delete(`${API_BASE}/clients/${clientId}/advisers/${adviserId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Links
  getLinks: (token, clientId) => 
    axios.get(`${API_BASE}/clients/${clientId}/links`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createLink: (token, clientId, linkData) =>
    axios.post(`${API_BASE}/clients/${clientId}/links`, linkData, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateLink: (token, clientId, linkId, linkData) =>
    axios.put(`${API_BASE}/clients/${clientId}/links/${linkId}`, linkData, { headers: { Authorization: `Bearer ${token}` } }),
  
  deleteLink: (token, clientId, linkId) =>
    axios.delete(`${API_BASE}/clients/${clientId}/links/${linkId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Workflow (Tasks)
  getWorkflow: (token, clientId) => 
    axios.get(`${API_BASE}/clients/${clientId}/workflow`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createTask: (token, clientId, taskData) =>
    axios.post(`${API_BASE}/clients/${clientId}/workflow`, taskData, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateTask: (token, clientId, taskId, taskData) =>
    axios.put(`${API_BASE}/clients/${clientId}/workflow/${taskId}`, taskData, { headers: { Authorization: `Bearer ${token}` } }),
  
  deleteTask: (token, clientId, taskId) =>
    axios.delete(`${API_BASE}/clients/${clientId}/workflow/${taskId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Activity
  getActivity: (token, clientId) => 
    axios.get(`${API_BASE}/clients/${clientId}/activity`, { headers: { Authorization: `Bearer ${token}` } }),

  // Audit
  getAudit: (token, clientId) => 
    axios.get(`${API_BASE}/clients/${clientId}/audit`, { headers: { Authorization: `Bearer ${token}` } }),

  // Governance
  getGovernance: (token, clientId) => 
    axios.get(`${API_BASE}/clients/${clientId}/governance`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createGovernance: (token, clientId, docData) =>
    axios.post(`${API_BASE}/clients/${clientId}/governance`, docData, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateGovernance: (token, clientId, docId, docData) =>
    axios.put(`${API_BASE}/clients/${clientId}/governance/${docId}`, docData, { headers: { Authorization: `Bearer ${token}` } }),
  
  deleteGovernance: (token, clientId, docId) =>
    axios.delete(`${API_BASE}/clients/${clientId}/governance/${docId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Advice
  getAdvice: (token, clientId) => 
    axios.get(`${API_BASE}/clients/${clientId}/advice`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createAdvice: (token, clientId, adviceData) =>
    axios.post(`${API_BASE}/clients/${clientId}/advice`, adviceData, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateAdvice: (token, clientId, adviceId, adviceData) =>
    axios.put(`${API_BASE}/clients/${clientId}/advice/${adviceId}`, adviceData, { headers: { Authorization: `Bearer ${token}` } }),
  
  deleteAdvice: (token, clientId, adviceId) =>
    axios.delete(`${API_BASE}/clients/${clientId}/advice/${adviceId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Search
  search: (token, query) => 
    axios.get(`${API_BASE}/search?q=${encodeURIComponent(query)}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Call Logs
  getCallLogs: (token, clientId) =>
    axios.get(`${API_BASE}/clients/${clientId}/call-logs`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getCallLog: (token, clientId, callLogId) =>
    axios.get(`${API_BASE}/clients/${clientId}/call-logs/${callLogId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createCallLog: (token, clientId, callLogData) =>
    axios.post(`${API_BASE}/clients/${clientId}/call-logs`, callLogData, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateCallLog: (token, clientId, callLogId, callLogData) =>
    axios.put(`${API_BASE}/clients/${clientId}/call-logs/${callLogId}`, callLogData, { headers: { Authorization: `Bearer ${token}` } }),
  
  deleteCallLog: (token, clientId, callLogId) =>
    axios.delete(`${API_BASE}/clients/${clientId}/call-logs/${callLogId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  sendCallSummaryEmail: (token, clientId, callLogId, recipientEmail, includeNotes = true) =>
    axios.post(`${API_BASE}/clients/${clientId}/call-logs/${callLogId}/send-summary`, 
      { recipient_email: recipientEmail, include_notes: includeNotes }, 
      { headers: { Authorization: `Bearer ${token}` } }),

  // Notes
  updateClientNotes: (token, clientId, notes) =>
    axios.put(`${API_BASE}/clients/${clientId}/notes`, { notes }, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateIndividualNotes: (token, clientId, individualId, notes) =>
    axios.put(`${API_BASE}/clients/${clientId}/individuals/${individualId}/notes`, { notes }, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateAdviserNotes: (token, clientId, adviserId, notes) =>
    axios.put(`${API_BASE}/clients/${clientId}/advisers/${adviserId}/notes`, { notes }, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateEntityNotes: (token, clientId, entityId, notes) =>
    axios.put(`${API_BASE}/clients/${clientId}/entities/${entityId}/notes`, { notes }, { headers: { Authorization: `Bearer ${token}` } }),

  // Organisations
  getOrganisations: (token) =>
    axios.get(`${API_BASE}/organisations`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getOrganisation: (token, orgId) =>
    axios.get(`${API_BASE}/organisations/${orgId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createOrganisation: (token, orgData) =>
    axios.post(`${API_BASE}/organisations`, orgData, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateOrganisation: (token, orgId, orgData) =>
    axios.put(`${API_BASE}/organisations/${orgId}`, orgData, { headers: { Authorization: `Bearer ${token}` } }),
  
  deleteOrganisation: (token, orgId) =>
    axios.delete(`${API_BASE}/organisations/${orgId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getOrganisationUsers: (token, orgId) =>
    axios.get(`${API_BASE}/organisations/${orgId}/users`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getOrganisationStats: (token, orgId) =>
    axios.get(`${API_BASE}/organisations/${orgId}/stats`, { headers: { Authorization: `Bearer ${token}` } }),

  // User Management
  getUsers: (token) =>
    axios.get(`${API_BASE}/auth/users`, { headers: { Authorization: `Bearer ${token}` } }),
  
  registerUser: (token, userData) =>
    axios.post(`${API_BASE}/auth/register`, userData, { headers: { Authorization: `Bearer ${token}` } }),

  // User Preferences
  getUserPreferences: (token) =>
    axios.get(`${API_BASE}/user/preferences`, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateUserPreferences: (token, preferences) =>
    axios.put(`${API_BASE}/user/preferences`, preferences, { headers: { Authorization: `Bearer ${token}` } }),

  // User Sessions
  getUserSessions: (token) =>
    axios.get(`${API_BASE}/user/sessions`, { headers: { Authorization: `Bearer ${token}` } }),
  
  signOutSession: (token, sessionId) =>
    axios.delete(`${API_BASE}/user/sessions/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  signOutAllSessions: (token) =>
    axios.delete(`${API_BASE}/user/sessions`, { headers: { Authorization: `Bearer ${token}` } }),

  // Organisation Access Settings
  getOrganisationAccessSettings: (token) =>
    axios.get(`${API_BASE}/organisation/access-settings`, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateOrganisationAccessSettings: (token, settings) =>
    axios.put(`${API_BASE}/organisation/access-settings`, settings, { headers: { Authorization: `Bearer ${token}` } }),

  // Export
  exportClientPack: (token, clientId, format = 'html', options = {}) => {
    const params = new URLSearchParams({
      format,
      include_individuals: options.includeIndividuals !== false,
      include_entities: options.includeEntities !== false,
      include_advisers: options.includeAdvisers !== false,
      include_activity: options.includeActivity || false
    });
    return axios.get(`${API_BASE}/export/clients/${clientId}/pack?${params}`, { 
      headers: { Authorization: `Bearer ${token}` },
      responseType: format === 'csv' ? 'blob' : 'text'
    });
  },

  exportIndividualsCSV: (token, clientId) =>
    axios.get(`${API_BASE}/export/clients/${clientId}/individuals/csv`, { 
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    }),

  exportEntitiesCSV: (token, clientId) =>
    axios.get(`${API_BASE}/export/clients/${clientId}/entities/csv`, { 
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    }),

  // Analytics
  getAnalyticsOverview: (token) =>
    axios.get(`${API_BASE}/analytics/overview`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getTaskTrends: (token) =>
    axios.get(`${API_BASE}/analytics/task-trends`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getActivityHeatmap: (token) =>
    axios.get(`${API_BASE}/analytics/activity-heatmap`, { headers: { Authorization: `Bearer ${token}` } }),

  // Access Requests
  getAccessRequests: (token, status) => {
    const params = status ? `?status=${status}` : '';
    return axios.get(`${API_BASE}/access-requests${params}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  
  getAccessRequestsPendingCount: (token) =>
    axios.get(`${API_BASE}/access-requests/pending/count`, { headers: { Authorization: `Bearer ${token}` } }),
  
  createAccessRequest: (token, data) =>
    axios.post(`${API_BASE}/access-requests`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  updateAccessRequest: (token, requestId, data) =>
    axios.put(`${API_BASE}/access-requests/${requestId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  cancelAccessRequest: (token, requestId) =>
    axios.delete(`${API_BASE}/access-requests/${requestId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  checkClientAccess: (token, clientId) =>
    axios.get(`${API_BASE}/access-requests/client/${clientId}/check`, { headers: { Authorization: `Bearer ${token}` } }),
  
  getClientAccessGrants: (token, clientId) =>
    axios.get(`${API_BASE}/access-requests/grants/client/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  revokeAccessGrant: (token, grantId) =>
    axios.delete(`${API_BASE}/access-requests/grants/${grantId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Client Documents
  getClientDocuments: (token, clientId) =>
    axios.get(`${API_BASE}/clients/${clientId}/documents`, { headers: { Authorization: `Bearer ${token}` } }),

  // Notifications
  getNotifications: (token, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${API_BASE}/notifications${qs ? '?' + qs : ''}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  getUnreadNotificationCount: (token) =>
    axios.get(`${API_BASE}/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } }),
  markNotificationRead: (token, id) =>
    axios.put(`${API_BASE}/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  markAllNotificationsRead: (token) =>
    axios.put(`${API_BASE}/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  generateNotifications: (token) =>
    axios.post(`${API_BASE}/notifications/generate`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  deleteNotification: (token, id) =>
    axios.delete(`${API_BASE}/notifications/${id}`, { headers: { Authorization: `Bearer ${token}` } }),

  // My Tasks (unified)
  getMyTasks: (token, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${API_BASE}/my-tasks${qs ? '?' + qs : ''}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  quickCapture: (token, data) =>
    axios.post(`${API_BASE}/my-tasks/quick-capture`, data, { headers: { Authorization: `Bearer ${token}` } }),
  getQuickCaptureContexts: (token) =>
    axios.get(`${API_BASE}/my-tasks/contexts`, { headers: { Authorization: `Bearer ${token}` } }),

  // Health Scores
  getHealthScores: (token) =>
    axios.get(`${API_BASE}/health-scores`, { headers: { Authorization: `Bearer ${token}` } }),
  getClientHealthScore: (token, clientId) =>
    axios.get(`${API_BASE}/health-scores/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Task Comments
  getTaskComments: (token, taskId) =>
    axios.get(`${API_BASE}/task-comments/${taskId}`, { headers: { Authorization: `Bearer ${token}` } }),
  createTaskComment: (token, data) =>
    axios.post(`${API_BASE}/task-comments`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteTaskComment: (token, commentId) =>
    axios.delete(`${API_BASE}/task-comments/${commentId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Saved Filters
  getSavedFilters: (token) =>
    axios.get(`${API_BASE}/saved-filters`, { headers: { Authorization: `Bearer ${token}` } }),
  createSavedFilter: (token, data) =>
    axios.post(`${API_BASE}/saved-filters`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteSavedFilter: (token, filterId) =>
    axios.delete(`${API_BASE}/saved-filters/${filterId}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Batch Tasks
  batchUpdateTasks: (token, data) =>
    axios.post(`${API_BASE}/batch-tasks/update`, data, { headers: { Authorization: `Bearer ${token}` } }),

  // Task Status (drag-drop)
  updateTaskStatus: (token, data) =>
    axios.put(`${API_BASE}/my-tasks/status`, data, { headers: { Authorization: `Bearer ${token}` } }),

  // Task Dependencies
  addTaskDependency: (token, data) =>
    axios.post(`${API_BASE}/my-tasks/dependencies`, data, { headers: { Authorization: `Bearer ${token}` } }),
  removeTaskDependency: (token, taskId, depTaskId, source) =>
    axios.delete(`${API_BASE}/my-tasks/dependencies/${taskId}/${depTaskId}?source=${source}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Client Intake
  getIntakeDrafts: (token) =>
    axios.get(`${API_BASE}/client-intake/drafts`, { headers: { Authorization: `Bearer ${token}` } }),
  createIntakeDraft: (token, data) =>
    axios.post(`${API_BASE}/client-intake/drafts`, data, { headers: { Authorization: `Bearer ${token}` } }),
  getIntakeDraft: (token, draftId) =>
    axios.get(`${API_BASE}/client-intake/drafts/${draftId}`, { headers: { Authorization: `Bearer ${token}` } }),
  updateIntakeDraft: (token, draftId, data) =>
    axios.put(`${API_BASE}/client-intake/drafts/${draftId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteIntakeDraft: (token, draftId) =>
    axios.delete(`${API_BASE}/client-intake/drafts/${draftId}`, { headers: { Authorization: `Bearer ${token}` } }),
  aiPreFillIntake: (token, data) =>
    axios.post(`${API_BASE}/client-intake/ai-prefill`, data, { headers: { Authorization: `Bearer ${token}` } }),
  finalizeIntake: (token, draftId) =>
    axios.post(`${API_BASE}/client-intake/finalize/${draftId}`, {}, { headers: { Authorization: `Bearer ${token}` } }),
};

export default api;
